/**
 * Calendar API Client — admin-server proxy to BE Agent Calendar API.
 *
 * Object literal pattern (matches existing api/users.ts, api/playgroundUsers.ts).
 * Axios `params` for query string (auto-encodes RFC3339 `+09:00` offset).
 * `satisfies` keeps the literal but enforces the interface shape.
 */

import { apiClient } from './client';

export type CalendarErrorCode =
  | 'BE_BUSINESS_ERROR'
  | 'USER_NOT_BE_REGISTERED'
  | 'EVENT_NOT_FOUND'
  | 'BE_VALIDATION_ERROR'
  | 'BE_AUTH_MISCONFIG'
  | 'BE_UNAVAILABLE';

/** Unified error envelope returned by admin-server CalendarAPIError handler. */
export interface CalendarErrorEnvelope {
  error_code: CalendarErrorCode;
  msg: string;
  detail?: Record<string, unknown>;
}

export interface CalendarEvent {
  event_id: number;
  summary: string;
  startDt: string;
  endDt?: string | null;
  calendarType: string;
  description?: string | null;
  color?: string | null;
  metadata: Record<string, unknown>;
  backend_source?: string | null;
  recurrenceRule?: string | null;
  regDt?: string | null;
  modDt?: string | null;
}

export interface CalendarDailyEvent {
  event_id: number;
  summary: string;
  calendarType: string;
  startDt: string;
  endDt?: string | null;
  color?: string | null;
  backend_source?: string | null;
  metadata: Record<string, unknown>;
  workoutSummary?: Record<string, unknown> | null;
}

export interface BusyBlock {
  start: string;
  end: string;
}

export interface CalendarEventCreate {
  summary: string;
  startDt: string;
  endDt?: string;
  description?: string;
  color?: string;
  metaJson?: string;
  recurrenceRule?: string;
}

export type CalendarEventUpdate = Partial<CalendarEventCreate>;

export type DailyEventGroups = Record<string, CalendarDailyEvent[]>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ApiFn = (...args: any[]) => Promise<unknown>;

export const calendarApi = {
  async listEvents(
    userId: string,
    start: string,
    end: string,
    type?: string,
  ): Promise<CalendarEvent[]> {
    const params: Record<string, string> = { start, end };
    if (type) params.type = type;
    const { data } = await apiClient.get<CalendarEvent[]>(
      `/users/${userId}/calendar/events`,
      { params },
    );
    return data;
  },

  async getEvent(userId: string, eventId: number): Promise<CalendarEvent> {
    const { data } = await apiClient.get<CalendarEvent>(
      `/users/${userId}/calendar/events/${eventId}`,
    );
    return data;
  },

  async createEvent(
    userId: string,
    body: CalendarEventCreate,
  ): Promise<CalendarEvent> {
    const { data } = await apiClient.post<CalendarEvent>(
      `/users/${userId}/calendar/events`,
      body,
    );
    return data;
  },

  async updateEvent(
    userId: string,
    eventId: number,
    body: CalendarEventUpdate,
  ): Promise<CalendarEvent> {
    const { data } = await apiClient.patch<CalendarEvent>(
      `/users/${userId}/calendar/events/${eventId}`,
      body,
    );
    return data;
  },

  async deleteEvent(userId: string, eventId: number): Promise<void> {
    await apiClient.delete(`/users/${userId}/calendar/events/${eventId}`);
  },

  async listGroupedDaily(
    userId: string,
    start: string,
    end: string,
  ): Promise<DailyEventGroups> {
    const { data } = await apiClient.get<DailyEventGroups>(
      `/users/${userId}/calendar/events/grouped/daily`,
      { params: { start, end } },
    );
    return data;
  },

  async getAvailability(
    userId: string,
    start: string,
    end: string,
  ): Promise<BusyBlock[]> {
    const { data } = await apiClient.get<BusyBlock[]>(
      `/users/${userId}/calendar/availability`,
      { params: { start, end } },
    );
    return data;
  },
} satisfies Record<string, ApiFn>;

/** Extract envelope from an axios error, if present. */
export function getCalendarErrorEnvelope(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  err: any,
): CalendarErrorEnvelope | null {
  const data = err?.response?.data;
  if (
    data &&
    typeof data === 'object' &&
    typeof data.error_code === 'string' &&
    typeof data.msg === 'string'
  ) {
    return data as CalendarErrorEnvelope;
  }
  return null;
}
